/**
 * useTwilioVideo Hook
 *
 * React hook for managing Twilio Video connections.
 * Handles room connection, local/remote tracks, and call controls.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import Video, {
  Room,
  LocalVideoTrack,
  LocalAudioTrack,
  RemoteParticipant,
  LocalParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Track,
  ConnectOptions,
  CreateLocalTracksOptions,
} from 'twilio-video';

export interface UseTwilioVideoOptions {
  /** JWT access token from backend */
  token: string | null;
  /** Room name to join */
  roomName: string | null;
  /** Called when successfully connected to room */
  onConnected?: () => void;
  /** Called when disconnected from room */
  onDisconnected?: (room: Room | null) => void;
  /** Called when an error occurs */
  onError?: (error: Error) => void;
  /** Called when a remote participant connects */
  onParticipantConnected?: (participant: RemoteParticipant) => void;
  /** Called when a remote participant disconnects */
  onParticipantDisconnected?: (participant: RemoteParticipant) => void;
  /** Start with audio muted */
  startMuted?: boolean;
  /** Start with video disabled */
  startVideoOff?: boolean;
  /** Enable network quality monitoring (0-5 scale) */
  enableNetworkQuality?: boolean;
  /** Publish a microphone track only — never ask for the camera (audio calls). */
  audioOnly?: boolean;
  /**
   * Preferred microphone / camera (a MediaDeviceInfo.deviceId). Optional and
   * off by default (telehealth never sets them): when the device is gone the
   * hook falls back to the browser default instead of failing.
   */
  audioDeviceId?: string | null;
  videoDeviceId?: string | null;
}

export interface UseTwilioVideoReturn {
  /** The connected Twilio Room instance */
  room: Room | null;
  /** The local participant */
  localParticipant: LocalParticipant | null;
  /** Array of remote participants */
  remoteParticipants: RemoteParticipant[];
  /** Whether currently connecting to room */
  isConnecting: boolean;
  /** Whether connected to room */
  isConnected: boolean;
  /** Any error that occurred */
  error: Error | null;
  /** Connect to the room */
  connect: () => Promise<void>;
  /** Disconnect from the room */
  disconnect: () => void;
  /** Toggle audio on/off */
  toggleAudio: () => void;
  /** Toggle video on/off */
  toggleVideo: () => void;
  /** Whether audio is currently enabled */
  isAudioEnabled: boolean;
  /** Whether video is currently enabled */
  isVideoEnabled: boolean;
  /** Ref for the local video element */
  localVideoRef: React.RefObject<HTMLVideoElement>;
  /** Local network quality level (0-5, null if not available) */
  networkQualityLevel: number | null;
  /** Dominant speaker (if available) */
  dominantSpeaker: RemoteParticipant | null;
  /**
   * Swap the published microphone / camera for `track` without leaving the
   * room (device pickers): unpublish + stop the old track of the same kind,
   * publish the new one, keep its mute / camera-off state. Unused by
   * telehealth; additive.
   */
  replaceLocalTrack: (track: LocalVideoTrack | LocalAudioTrack) => Promise<void>;
}

export function useTwilioVideo(options: UseTwilioVideoOptions): UseTwilioVideoReturn {
  const {
    token,
    roomName,
    onConnected,
    onDisconnected,
    onError,
    onParticipantConnected,
    onParticipantDisconnected,
    startMuted = false,
    startVideoOff = false,
    enableNetworkQuality = true,
    audioOnly = false,
    audioDeviceId = null,
    videoDeviceId = null,
  } = options;

  // State
  const [room, setRoom] = useState<Room | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [remoteParticipants, setRemoteParticipants] = useState<RemoteParticipant[]>([]);
  const [isAudioEnabled, setIsAudioEnabled] = useState(!startMuted);
  const [isVideoEnabled, setIsVideoEnabled] = useState(!startVideoOff);
  const [networkQualityLevel, setNetworkQualityLevel] = useState<number | null>(null);
  const [dominantSpeaker, setDominantSpeaker] = useState<RemoteParticipant | null>(null);

  // Refs
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const localTracksRef = useRef<(LocalVideoTrack | LocalAudioTrack)[]>([]);
  const roomRef = useRef<Room | null>(null);
  // Bumped by disconnect(): a connect() still in flight (creating tracks,
  // joining the room) must not leave a live room or a lit camera behind.
  const connectGenRef = useRef(0);

  /**
   * Create local audio and video tracks
   */
  const createLocalTracks = useCallback(async (): Promise<(LocalVideoTrack | LocalAudioTrack)[]> => {
    const audioConstraint = audioDeviceId ? { deviceId: { exact: audioDeviceId } } : undefined;
    const videoSize = {
      width: { ideal: 1280, max: 1920, min: 640 },
      height: { ideal: 720, max: 1080, min: 480 },
      frameRate: { ideal: 24, max: 30 },
    };
    // A remembered device that is no longer plugged in must not fail the call.
    const preferred = async <T,>(withDevice: () => Promise<T>, fallback: () => Promise<T>): Promise<T> => {
      if (!audioDeviceId && !videoDeviceId) return withDevice();
      try {
        return await withDevice();
      } catch (err) {
        console.warn('Preferred device unavailable, using the default:', err);
        return fallback();
      }
    };
    if (audioOnly) {
      return [
        await preferred(
          () => (audioConstraint ? Video.createLocalAudioTrack(audioConstraint) : Video.createLocalAudioTrack()),
          () => Video.createLocalAudioTrack(),
        ),
      ];
    }
    const trackOptions: CreateLocalTracksOptions = {
      audio: audioConstraint ?? true,
      video: videoDeviceId ? { ...videoSize, deviceId: { exact: videoDeviceId } } : videoSize,
    };

    try {
      const tracks = await preferred(
        () => Video.createLocalTracks(trackOptions),
        () => Video.createLocalTracks({ audio: true, video: videoSize }),
      );
      return tracks as (LocalVideoTrack | LocalAudioTrack)[];
    } catch (err) {
      // If video fails, try audio only
      console.warn('Failed to get video, trying audio only:', err);
      try {
        const audioTrack = await Video.createLocalAudioTrack();
        return [audioTrack];
      } catch (audioErr) {
        console.error('Failed to get any media tracks:', audioErr);
        throw audioErr;
      }
    }
  }, [audioOnly, audioDeviceId, videoDeviceId]);

  /**
   * Attach local video to the video element
   */
  const attachLocalVideo = useCallback((videoTrack: LocalVideoTrack) => {
    if (localVideoRef.current && videoTrack) {
      // Detach from any previous elements
      videoTrack.detach().forEach((el) => el.remove());
      // Attach to our element
      videoTrack.attach(localVideoRef.current);
    }
  }, []);

  /**
   * Connect to the Twilio Video room
   */
  const connect = useCallback(async () => {
    if (!token || !roomName) {
      console.warn('Cannot connect: token or roomName not provided');
      return;
    }

    if (isConnected || isConnecting) {
      console.warn('Already connected or connecting');
      return;
    }

    setIsConnecting(true);
    setError(null);
    const gen = ++connectGenRef.current;
    const stale = () => gen !== connectGenRef.current;

    try {
      // Create local tracks
      const localTracks = await createLocalTracks();
      if (stale()) {
        // disconnect() ran while the camera / microphone were opening.
        localTracks.forEach((track) => track.stop());
        return;
      }
      localTracksRef.current = localTracks;

      // Attach local video preview
      const videoTrack = localTracks.find((t) => t.kind === 'video') as LocalVideoTrack | undefined;
      if (videoTrack) {
        attachLocalVideo(videoTrack);
        // Apply initial video state
        if (startVideoOff) {
          videoTrack.disable();
          setIsVideoEnabled(false);
        }
      }

      // Apply initial audio state
      const audioTrack = localTracks.find((t) => t.kind === 'audio') as LocalAudioTrack | undefined;
      if (audioTrack && startMuted) {
        audioTrack.disable();
        setIsAudioEnabled(false);
      }

      // Connect options
      const connectOptions: ConnectOptions = {
        name: roomName,
        tracks: localTracks,
        dominantSpeaker: true,
      };

      if (enableNetworkQuality) {
        connectOptions.networkQuality = {
          local: 1, // Report local network quality
          remote: 1, // Report remote network quality
        };
      }

      // Connect to room
      const connectedRoom = await Video.connect(token, connectOptions);
      if (stale()) {
        // disconnect() ran while joining: leave the room we just entered.
        connectedRoom.disconnect();
        localTracks.forEach((track) => track.stop());
        return;
      }
      roomRef.current = connectedRoom;
      setRoom(connectedRoom);
      setIsConnected(true);

      // Set initial remote participants
      const existingParticipants = Array.from(connectedRoom.participants.values());
      setRemoteParticipants(existingParticipants);

      // Room event handlers
      connectedRoom.on('participantConnected', (participant: RemoteParticipant) => {
        console.log('Participant connected:', participant.identity);
        setRemoteParticipants((prev) => [...prev, participant]);
        onParticipantConnected?.(participant);
      });

      connectedRoom.on('participantDisconnected', (participant: RemoteParticipant) => {
        console.log('Participant disconnected:', participant.identity);
        setRemoteParticipants((prev) => prev.filter((p) => p.sid !== participant.sid));
        onParticipantDisconnected?.(participant);
      });

      connectedRoom.on('disconnected', (room: Room, error?: Error) => {
        console.log('Disconnected from room:', room.name, error?.message);
        setIsConnected(false);
        setRoom(null);
        setRemoteParticipants([]);
        onDisconnected?.(room);

        // Clean up local tracks
        localTracksRef.current.forEach((track) => {
          track.stop();
          track.detach().forEach((el) => el.remove());
        });
        localTracksRef.current = [];

        if (error) {
          setError(error);
          onError?.(error);
        }
      });

      // Dominant speaker tracking
      connectedRoom.on('dominantSpeakerChanged', (participant: RemoteParticipant | null) => {
        setDominantSpeaker(participant);
      });

      // Network quality tracking
      if (enableNetworkQuality && connectedRoom.localParticipant) {
        connectedRoom.localParticipant.on(
          'networkQualityLevelChanged',
          (level: number) => {
            setNetworkQualityLevel(level);
          }
        );
      }

      console.log('Connected to room:', connectedRoom.name);
      onConnected?.();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to connect to video room');
      console.error('Failed to connect:', error);
      setError(error);
      onError?.(error);

      // Clean up any tracks we created
      localTracksRef.current.forEach((track) => {
        track.stop();
      });
      localTracksRef.current = [];
    } finally {
      setIsConnecting(false);
    }
  }, [
    token,
    roomName,
    isConnected,
    isConnecting,
    createLocalTracks,
    attachLocalVideo,
    startVideoOff,
    startMuted,
    enableNetworkQuality,
    onConnected,
    onDisconnected,
    onError,
    onParticipantConnected,
    onParticipantDisconnected,
  ]);

  /**
   * Disconnect from the room
   */
  const disconnect = useCallback(() => {
    connectGenRef.current += 1;
    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }

    // Stop and detach all local tracks
    localTracksRef.current.forEach((track) => {
      track.stop();
      track.detach().forEach((el) => el.remove());
    });
    localTracksRef.current = [];

    setRoom(null);
    setIsConnected(false);
    setRemoteParticipants([]);
    setDominantSpeaker(null);
    setNetworkQualityLevel(null);
  }, []);

  /**
   * Toggle local audio track
   */
  const toggleAudio = useCallback(() => {
    const audioTrack = localTracksRef.current.find((t) => t.kind === 'audio') as
      | LocalAudioTrack
      | undefined;

    if (audioTrack) {
      if (audioTrack.isEnabled) {
        audioTrack.disable();
        setIsAudioEnabled(false);
      } else {
        audioTrack.enable();
        setIsAudioEnabled(true);
      }
    }
  }, []);

  /**
   * Toggle local video track
   */
  const toggleVideo = useCallback(() => {
    const videoTrack = localTracksRef.current.find((t) => t.kind === 'video') as
      | LocalVideoTrack
      | undefined;

    if (videoTrack) {
      if (videoTrack.isEnabled) {
        videoTrack.disable();
        setIsVideoEnabled(false);
      } else {
        videoTrack.enable();
        setIsVideoEnabled(true);
      }
    }
  }, []);

  /**
   * Replace the published microphone / camera (device pickers)
   */
  const replaceLocalTrack = useCallback(
    async (track: LocalVideoTrack | LocalAudioTrack) => {
      const old = localTracksRef.current.find((t) => t.kind === track.kind);
      const wasEnabled = old ? old.isEnabled : track.kind === 'audio' ? isAudioEnabled : isVideoEnabled;
      const participant = roomRef.current?.localParticipant;
      if (old) {
        try {
          participant?.unpublishTrack(old);
        } catch {
          /* the room may already be gone */
        }
        old.stop();
        old.detach().forEach((el) => {
          if (el !== localVideoRef.current) el.remove();
        });
      }
      if (!wasEnabled) track.disable();
      localTracksRef.current = [...localTracksRef.current.filter((t) => t !== old), track];
      if (track.kind === 'video') attachLocalVideo(track as LocalVideoTrack);
      if (participant) await participant.publishTrack(track);
    },
    [attachLocalVideo, isAudioEnabled, isVideoEnabled],
  );

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    room,
    localParticipant: room?.localParticipant || null,
    remoteParticipants,
    isConnecting,
    isConnected,
    error,
    connect,
    disconnect,
    toggleAudio,
    toggleVideo,
    isAudioEnabled,
    isVideoEnabled,
    localVideoRef,
    networkQualityLevel,
    dominantSpeaker,
    replaceLocalTrack,
  };
}

export default useTwilioVideo;
